import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useIOSScrollFix } from '../../hooks/useIOSScrollFix';
import {
  AuthContainer,
  Logo,
  AuthBox,
  FormContainer,
  LinkContainer,
} from '../../styles/Auth.styles';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [timeoutMessage, setTimeoutMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use iOS-specific scroll fix hook
  const { forceIOSScrollReset } = useIOSScrollFix();
  
  // Check for timeout query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const timeout = params.get('timeout');
    
    if (timeout === 'idle') {
      setTimeoutMessage('Your session has expired due to inactivity. Please log in again.');
    }
  }, [location]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await login(username, password);
      
      // Force iOS scroll reset before navigation
      forceIOSScrollReset();
      
      // Check if password change is required for first-time login
      if (result.requiresPasswordChange) {
        // Redirect to password change page with userId and email
        navigate('/change-password', { 
          state: { 
            userId: result.userId, 
            email: result.email
          } 
        });
      }
      // Check if 2FA is required
      else if (result.requiresTwoFactor) {
        // Redirect to 2FA verification page with userId, email, and other 2FA info
        navigate('/verify-2fa', {
          state: { 
            userId: result.userId, 
            email: result.email,
            emailSent: result.emailSent,
            emailError: result.emailError,
            adminFallbackCode: result.adminFallbackCode
          } 
        });
      } else {
        // Normal login flow - navigate to appropriate page
        if (result.role === 'admin' || result.role === 'accountant') {
          navigate('/reporting');
        } else {
          navigate('/timecard');
        }
      }
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <AuthContainer>
      <Logo
        src="/images/eci_logo_color.svg"
        alt="ECI Logo"
      />
      <AuthBox>
        <Typography
          variant="caption"
          align="center"
          display="block"
          sx={{ color: '#666666', mb: 0, lineHeight: 1.1 }}
        >
          Expert Consultants Incorporated
        </Typography>
        <Typography
          component="h1"
          variant="h5"
          align="center"
          gutterBottom
          sx={{ color: '#666666', fontSize: '1.6rem' }}
        >
          Time Portal Login
        </Typography>
        <FormContainer component="form" onSubmit={handleSubmit}>
          {timeoutMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {timeoutMessage}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            margin="normal"
            required
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            autoFocus
            inputProps={{
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: 'false',
              style: { textTransform: 'lowercase' }
            }}
            sx={{
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: 'inherit !important',
                backgroundColor: 'transparent !important',
                transition: 'background-color 5000s ease-in-out 0s !important',
              },
              '& input:-webkit-autofill:hover': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: 'inherit !important',
                backgroundColor: 'transparent !important',
                transition: 'background-color 5000s ease-in-out 0s !important',
              },
              '& input:-webkit-autofill:focus': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: 'inherit !important',
                backgroundColor: 'transparent !important',
                transition: 'background-color 5000s ease-in-out 0s !important',
              },
              '& input:-webkit-autofill:active': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: 'inherit !important',
                backgroundColor: 'transparent !important',
                transition: 'background-color 5000s ease-in-out 0s !important',
              },
              '& .MuiInputLabel-root': {
                zIndex: 1,
                pointerEvents: 'none',
              },
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: 'inherit !important',
                backgroundColor: 'transparent !important',
                transition: 'background-color 5000s ease-in-out 0s !important',
              },
              '& input:-webkit-autofill:hover': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: 'inherit !important',
                backgroundColor: 'transparent !important',
                transition: 'background-color 5000s ease-in-out 0s !important',
              },
              '& input:-webkit-autofill:focus': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: 'inherit !important',
                backgroundColor: 'transparent !important',
                transition: 'background-color 5000s ease-in-out 0s !important',
              },
              '& input:-webkit-autofill:active': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: 'inherit !important',
                backgroundColor: 'transparent !important',
                transition: 'background-color 5000s ease-in-out 0s !important',
              },
              '& .MuiInputLabel-root': {
                zIndex: 1,
                pointerEvents: 'none',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Log in
          </Button>
          <LinkContainer>
            <Link to="/forgot-password">
              I forgot my password
            </Link>
          </LinkContainer>
        </FormContainer>
      </AuthBox>
    </AuthContainer>
  );
};

export default Login;
